import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { organizationCloseStageContract } from "@common/contracts/organization-close-stage.contract";
import {
    OrganizationCloseStage,
    OrganizationCloseStageSchema,
} from "@common/types/organizationCloseStage";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";

const organizationCloseStageClient = getBackendAPIClient(
    organizationCloseStageContract,
);

const organizationCloseStageQueryKeys = {
    all: () => ["organization-close-stage"] as const,
    organization: (organizationId: string) =>
        [
            ...organizationCloseStageQueryKeys.all(),
            "organization",
            organizationId,
        ] as const,
    list: (organizationId: string) =>
        [
            ...organizationCloseStageQueryKeys.organization(organizationId),
            "list",
        ] as const,
};

export function useOrganizationCloseStages(options?: { enabled?: boolean }) {
    const { organizationId } = useScope();
    const isEnabled = options?.enabled ?? true;

    return useQuery({
        queryKey: organizationId
            ? organizationCloseStageQueryKeys.list(organizationId)
            : [],
        queryFn: async () => {
            if (!organizationId) {
                return [] as OrganizationCloseStage[];
            }

            const response =
                await organizationCloseStageClient.listOrganizationCloseStages({
                    params: { organizationId },
                });

            return response.body.closeStages.map((cs) =>
                OrganizationCloseStageSchema.parse(cs),
            );
        },
        enabled: !!organizationId && isEnabled,
    });
}

/** A copy of the close stages in their organization order. */
export const sortCloseStagesByOrder = (
    closeStages: readonly OrganizationCloseStage[],
): OrganizationCloseStage[] =>
    [...closeStages].sort((stageA, stageB) => stageA.order - stageB.order);

export function useSortedCloseStages(): OrganizationCloseStage[] {
    const { data: closeStages = [] } = useOrganizationCloseStages();
    return useMemo(() => sortCloseStagesByOrder(closeStages), [closeStages]);
}

export function useCreateOrganizationCloseStageMutation() {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof organizationCloseStageContract.createOrganizationCloseStage
            >["body"],
        ) => {
            if (!organizationId) {
                throw new Error("No active organization");
            }

            const response =
                await organizationCloseStageClient.createOrganizationCloseStage(
                    {
                        params: { organizationId },
                        body,
                    },
                );

            return OrganizationCloseStageSchema.parse(response.body);
        },
        onSuccess: async () => {
            if (!organizationId) {
                return;
            }

            await queryClient.invalidateQueries({
                queryKey:
                    organizationCloseStageQueryKeys.organization(
                        organizationId,
                    ),
                exact: false,
            });
        },
    });
}

export function useUpdateOrganizationCloseStageMutation(
    closeStage?: OrganizationCloseStage,
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof organizationCloseStageContract.updateOrganizationCloseStage
            >["body"],
        ) => {
            if (!closeStage) {
                throw new Error("No close stage selected");
            }

            const response =
                await organizationCloseStageClient.updateOrganizationCloseStage(
                    {
                        params: {
                            organizationId: closeStage.organizationId,
                            closeStageId: closeStage.id,
                        },
                        body,
                    },
                );

            return OrganizationCloseStageSchema.parse(response.body);
        },
        onSuccess: async () => {
            if (!closeStage) {
                return;
            }

            await queryClient.invalidateQueries({
                queryKey: organizationCloseStageQueryKeys.organization(
                    closeStage.organizationId,
                ),
                exact: false,
            });
        },
    });
}

export function useDeleteOrganizationCloseStageMutation(
    closeStage?: OrganizationCloseStage,
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            if (!closeStage) {
                throw new Error("No close stage selected");
            }

            await organizationCloseStageClient.deleteOrganizationCloseStage({
                params: {
                    organizationId: closeStage.organizationId,
                    closeStageId: closeStage.id,
                },
            });
        },
        onSuccess: async () => {
            if (!closeStage) {
                return;
            }

            await queryClient.invalidateQueries({
                queryKey: organizationCloseStageQueryKeys.organization(
                    closeStage.organizationId,
                ),
                exact: false,
            });
        },
    });
}

export function useChangeOrganizationCloseStagePositionMutation() {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async ({
            closeStageId,
            parentCloseStageId,
            order,
        }: {
            closeStageId: string;
            parentCloseStageId: string | null;
            order: number;
        }) => {
            if (!organizationId) {
                throw new Error("No active organization");
            }

            const response =
                await organizationCloseStageClient.changeOrganizationCloseStagePosition(
                    {
                        params: { organizationId, closeStageId },
                        body: { parentCloseStageId, order },
                    },
                );

            return OrganizationCloseStageSchema.parse(response.body);
        },
        onSuccess: async () => {
            if (!organizationId) {
                return;
            }

            await queryClient.invalidateQueries({
                queryKey:
                    organizationCloseStageQueryKeys.organization(
                        organizationId,
                    ),
                exact: false,
            });
        },
    });
}
